/**
 * Permissions v2 registry — the single source of truth for every permission
 * key in a multi-therapist clinic (roles, Hebrew labels, defaults, grouping).
 *
 * ⚠️ This file is the AUTHORED source. `functions/permissionsRegistry.js` is a
 * GENERATED CommonJS mirror (run `node scripts/syncPermissionsRegistry.js`);
 * `permissionsRegistry.sync.test.js` fails when the mirror is stale.
 * Keep this file dependency-free and limited to top-level
 * `export const` / `export function` so the generator can convert it.
 *
 * Backward-compat contract (non-negotiable):
 *   - `legacyDefault` is what an ABSENT value means for an existing clinic and
 *     MUST equal today's behavior for a non-admin therapist.
 *   - Admin (role === "owner" || isAdmin === true) bypasses every
 *     per-therapist key. Clinic-master keys are clinic-level only.
 *   - Every boolean is read with `v !== false && v !== null` (Firestore stores
 *     both). Never `!!v`.
 *
 * See muti_clinic_update.md §2 and DEVELOPER_GUIDE.md "Permissions v2".
 */

export const PERMISSIONS_SCHEMA_VERSION = 2;

/** Clinic roles (stored on `therapist.clinicRole`, optional on legacy docs). */
export const CLINIC_ROLES = ["owner", "manager", "therapist", "secretary"];

export const CLINIC_ROLE_LABELS = {
  owner: "בעל/ת הקליניקה",
  manager: "מנהל/ת קליניקה",
  therapist: "מטפל/ת",
  secretary: "מזכירות",
};

/** Short badge labels for cards/tables. */
export const CLINIC_ROLE_BADGES = {
  owner: "בעלים",
  manager: "מנהל/ת",
  therapist: "מטפל/ת",
  secretary: "מזכירות",
};

/** Roles that may be assigned via the Team UI (`owner` is never assignable). */
export const ASSIGNABLE_CLINIC_ROLES = ["manager", "therapist", "secretary"];

/** UI group order + Hebrew titles. */
export const PERMISSION_GROUPS = [
  { id: "patients", label: "מטופלים" },
  { id: "clinical", label: "מידע רפואי" },
  { id: "treatments", label: "דוחות טיפול" },
  { id: "calendar", label: "יומן" },
  { id: "finance", label: "פיננסים" },
  { id: "tasks", label: "משימות" },
  { id: "leads", label: "לידים" },
  { id: "reports", label: "דוחות" },
  { id: "team", label: "צוות" },
  { id: "settings", label: "הגדרות" },
  { id: "clinicMaster", label: "הגדרות קליניקה (כלל-קליניקתיות)" },
];

/**
 * `sensitive` levels (drive the ⚠ / ⚠⚠ UI markers):
 *   false      — no cross-therapist data exposure
 *   "personal" — shows personal data of patients not assigned to the therapist (⚠)
 *   "medical"  — shows medical content: clinical notes / treatment reports (⚠⚠)
 */
export const SENSITIVITY = { NONE: false, PERSONAL: "personal", MEDICAL: "medical" };

/**
 * Registry entries. Field reference:
 *   key, group, label, description
 *   legacyDefault            — meaning of "absent" for existing clinics (== today)
 *   roleDefaults             — preset applied when an admin picks a role
 *   roleDefaultsWhenAllShared— optional preset override when
 *                              clinic.multiClinicSettings.patientSharingMode === "all"
 *   enforcement              — ["client" | "rules" | "functions"]
 *   sensitive                — see SENSITIVITY
 *   requires                 — key that must resolve true before this one may be true
 *   clinicMaster             — clinic-level only (not per therapist)
 *   locked                   — manager-only in v1; rendered disabled for therapist/secretary
 *   type                     — "boolean" (default) | "enum"
 *   storage                  — clinic-master only: "therapistPermissions" | "multiClinicSettings"
 *   field                    — clinic-master only: field name inside `storage`
 *   values / default         — enum master keys
 *   since                    — schema version that introduced it
 */
export const PERMISSIONS_REGISTRY = [
  // ---------------------------------------------------------------- patients
  {
    key: "patients.viewAllClinic",
    group: "patients",
    label: "צפייה בכל מטופלי הקליניקה",
    description: "כרטיס מטופל בלבד — ללא קבצים ודוחות טיפול",
    legacyDefault: false,
    roleDefaults: { therapist: false, secretary: true },
    enforcement: ["client", "rules"],
    sensitive: "personal",
    requires: null,
    clinicMaster: false,
    since: 2,
  },
  {
    key: "patients.createForOthers",
    group: "patients",
    label: "יצירת מטופל ושיוכו למטפל אחר",
    description: "פתיחת כרטיס מטופל חדש ושיוכו למטפל/ת אחר/ת בקליניקה",
    legacyDefault: false,
    roleDefaults: { therapist: false, secretary: true },
    enforcement: ["client", "rules"],
    sensitive: false,
    requires: null,
    clinicMaster: false,
    since: 2,
  },
  {
    key: "patients.reassign",
    group: "patients",
    label: "העברת מטופל בין מטפלים",
    description: "שינוי המטפל/ת המשויך/ת למטופל קיים",
    legacyDefault: false,
    roleDefaults: { therapist: false, secretary: false },
    enforcement: ["client", "functions"],
    sensitive: false,
    requires: null,
    clinicMaster: false,
    since: 2,
  },
  {
    key: "patients.editOthers",
    group: "patients",
    label: "עריכת פרטי מטופלים של מטפלים אחרים",
    description: "עריכת פרטי קשר ופרטים אישיים בכרטיס מטופל שאינו משויך אליך",
    legacyDefault: false,
    roleDefaults: { therapist: false, secretary: true },
    enforcement: ["client", "rules"],
    sensitive: false,
    requires: null,
    clinicMaster: false,
    since: 2,
  },
  // ---------------------------------------------------------------- clinical
  {
    key: "clinical.viewSensitive",
    group: "clinical",
    label: "גישה למידע רפואי רגיש",
    description: "הערות קליניות ודוחות טיפול. כשכבוי — גם של המטופלים האישיים",
    legacyDefault: true,
    roleDefaults: { therapist: true, secretary: false },
    enforcement: ["client", "functions"],
    sensitive: "medical",
    requires: null,
    clinicMaster: false,
    since: 2,
  },
  // -------------------------------------------------------------- treatments
  {
    key: "treatments.write",
    group: "treatments",
    label: "כתיבת דוחות טיפול",
    description: "פתיחה וכתיבה של דוח טיפול למטופלים האישיים",
    legacyDefault: true,
    roleDefaults: { therapist: true, secretary: false },
    enforcement: ["client"],
    sensitive: false,
    requires: "clinical.viewSensitive",
    clinicMaster: false,
    since: 2,
  },
  {
    key: "treatments.viewSharedPatient",
    group: "treatments",
    label: "צפייה בדוחות טיפול של מטפלים אחרים במטופל משותף",
    description: "רלוונטי רק כאשר 'שיוך מטופל לכמה מטפלים' פעיל בקליניקה",
    legacyDefault: true,
    roleDefaults: { therapist: true, secretary: false },
    enforcement: ["client", "rules", "functions"],
    sensitive: "medical",
    requires: "clinical.viewSensitive",
    clinicMaster: false,
    since: 2,
  },
  {
    key: "treatments.viewAllClinic",
    group: "treatments",
    label: "צפייה בכל דוחות הטיפול בקליניקה",
    description: "קריאה בלבד של דוחות טיפול שנכתבו על ידי כל המטפלים",
    legacyDefault: false,
    roleDefaults: { therapist: false, secretary: false },
    enforcement: ["client", "rules", "functions"],
    sensitive: "medical",
    requires: "clinical.viewSensitive",
    clinicMaster: false,
    since: 2,
  },
  // ---------------------------------------------------------------- calendar
  {
    key: "calendar.viewClinic",
    group: "calendar",
    label: "צפייה ביומן הקליניקה (כל המטפלים)",
    description: "רואה את התורים של כל המטפלים (שמות מטופלים בלבד, ללא דוחות טיפול)",
    legacyDefault: false,
    roleDefaults: { therapist: false, secretary: true },
    roleDefaultsWhenAllShared: { therapist: true },
    enforcement: ["client", "rules"],
    sensitive: "personal",
    requires: null,
    clinicMaster: false,
    since: 2,
  },
  {
    key: "calendar.manageOthersAppointments",
    group: "calendar",
    label: "קביעה, עריכה וביטול תורים למטפלים אחרים",
    description: "ניהול מלא של תורים ביומנים של מטפלים אחרים",
    legacyDefault: false,
    roleDefaults: { therapist: false, secretary: true },
    enforcement: ["client", "rules"],
    sensitive: false,
    requires: null,
    clinicMaster: false,
    since: 2,
  },
  {
    key: "calendar.editOthersAvailability",
    group: "calendar",
    label: "עריכת שעות עבודה וחסימות של מטפלים אחרים",
    description: "שינוי זמינות שבועית וחסימות זמן ביומנים של מטפלים אחרים",
    legacyDefault: false,
    roleDefaults: { therapist: false, secretary: false },
    enforcement: ["client", "rules"],
    sensitive: false,
    requires: null,
    clinicMaster: false,
    since: 2,
  },
  {
    key: "calendar.manageAbsences",
    group: "calendar",
    label: "ניהול היעדרויות של מטפלים אחרים",
    description: "הוספה ועריכה של חופשות והיעדרויות עבור מטפלים אחרים",
    legacyDefault: false,
    roleDefaults: { therapist: false, secretary: true },
    enforcement: ["client", "rules"],
    sensitive: false,
    requires: null,
    clinicMaster: false,
    since: 2,
  },
  // ----------------------------------------------------------------- finance
  {
    key: "allowFinanceAccess",
    group: "finance",
    label: "גישה לפיננסים (התשלומים שלי)",
    description: "עמוד פיננסים, סכומי תשלום ותעריפי מטופלים — עבור התורים האישיים",
    legacyDefault: true,
    roleDefaults: { therapist: true, secretary: true },
    enforcement: ["client", "rules", "functions"],
    sensitive: false,
    requires: null,
    clinicMaster: false,
    since: 1,
  },
  {
    key: "allowCreateInvoices",
    group: "finance",
    label: "הפקת חשבוניות",
    description: "הפקת חשבוניות דרך חשבונית ירוקה / Digital Invoice",
    legacyDefault: true,
    roleDefaults: { therapist: true, secretary: true },
    enforcement: ["client", "functions"],
    sensitive: false,
    // No `requires` on purpose: today a therapist may create invoices even
    // when allowFinanceAccess is off (legacy behavior must not change).
    requires: null,
    clinicMaster: false,
    since: 1,
  },
  {
    key: "finance.viewClinicWide",
    group: "finance",
    label: "צפייה בתשלומים של כל המטפלים",
    description: "עמוד פיננסים ודוחות פיננסיים ברמת כל הקליניקה",
    legacyDefault: false,
    roleDefaults: { therapist: false, secretary: false },
    enforcement: ["client", "rules", "functions"],
    sensitive: "personal",
    requires: "allowFinanceAccess",
    clinicMaster: false,
    since: 2,
  },
  {
    key: "finance.collectForOthers",
    group: "finance",
    label: "רישום תשלום עבור תור של מטפל אחר",
    description: "גבייה ורישום תשלומים על תורים ביומנים של מטפלים אחרים",
    legacyDefault: false,
    roleDefaults: { therapist: false, secretary: true },
    enforcement: ["client", "rules"],
    sensitive: false,
    requires: "allowFinanceAccess",
    clinicMaster: false,
    since: 2,
  },
  {
    key: "finance.collectViaGrow",
    group: "finance",
    label: "שליחת דרישות תשלום (Grow)",
    description: "יצירה, שליחה וביטול של קישורי תשלום Grow למטופלים; דורש גישה לפיננסים וקליניקה מחוברת ל-Grow",
    legacyDefault: true,
    roleDefaults: { therapist: true, secretary: true },
    enforcement: ["client", "rules", "functions"],
    sensitive: false,
    requires: "allowFinanceAccess",
    clinicMaster: false,
    since: 2,
  },
  // ------------------------------------------------------------------- tasks
  {
    key: "tasks.assignToOthers",
    group: "tasks",
    label: "הקצאת משימות למטפלים אחרים",
    description: "בחירת מטפל/ת אחר/ת כאחראי/ת על משימה",
    legacyDefault: false,
    roleDefaults: { therapist: false, secretary: true },
    enforcement: ["client"],
    sensitive: false,
    requires: null,
    clinicMaster: false,
    since: 2,
  },
  {
    key: "tasks.viewAllClinic",
    group: "tasks",
    label: "צפייה בכל משימות הקליניקה",
    description: "כולל משימות אישיות של מטפלים אחרים",
    legacyDefault: false,
    roleDefaults: { therapist: false, secretary: true },
    enforcement: ["client", "rules"],
    sensitive: false,
    requires: null,
    clinicMaster: false,
    since: 2,
  },
  // ------------------------------------------------------------------- leads
  // Leads v2 (leads_plan.md §4.1): all leads are CLINIC leads — there is no
  // per-therapist assignment and no per-user toggle. ONE clinic-level switch
  // (multiClinicSettings.therapistsCanManageLeads) decides whether the whole
  // team sees + edits the list; admins always do. Absent ⇒ true (legacy).
  {
    key: "leads.access",
    group: "clinicMaster",
    label: "מטפלים רואים ועורכים מתעניינים",
    description: "כבוי — רק מנהלים; פעיל — כל הצוות רואה ועורך את כל המתעניינים",
    legacyDefault: true,
    enforcement: ["client", "rules", "functions"],
    sensitive: false,
    requires: null,
    clinicMaster: true,
    storage: "multiClinicSettings",
    field: "therapistsCanManageLeads",
    since: 2,
  },
  // ----------------------------------------------------------------- reports
  {
    key: "allowExportData",
    group: "reports",
    label: "ייצוא נתונים ודוחות",
    description: "ייצוא CSV ודוחות PDF",
    legacyDefault: true,
    roleDefaults: { therapist: true, secretary: false },
    enforcement: ["client", "functions"],
    sensitive: false,
    requires: null,
    clinicMaster: false,
    since: 1,
  },
  {
    key: "reports.viewClinicWide",
    group: "reports",
    label: "צפייה בדוחות של כל הקליניקה",
    description: "דוחות חודשיים ברמת כל המטפלים",
    legacyDefault: false,
    roleDefaults: { therapist: false, secretary: false },
    enforcement: ["client", "functions"],
    sensitive: "personal",
    requires: null,
    clinicMaster: false,
    since: 2,
  },
  // -------------------------------------------------------------------- team
  {
    key: "team.manage",
    group: "team",
    label: "ניהול צוות",
    description: "הוספה, השבתה והרשאות של אנשי צוות",
    legacyDefault: false,
    roleDefaults: { therapist: false, secretary: false },
    enforcement: ["client"],
    sensitive: false,
    requires: null,
    clinicMaster: false,
    locked: true,
    since: 2,
  },
  // ---------------------------------------------------------------- settings
  {
    key: "settings.manageClinic",
    group: "settings",
    label: "הגדרות קליניקה",
    description: "עריכת הגדרות הקליניקה",
    legacyDefault: false,
    roleDefaults: { therapist: false, secretary: false },
    enforcement: ["client"],
    sensitive: false,
    requires: null,
    clinicMaster: false,
    locked: true,
    since: 2,
  },
  // ----------------------------------------------------------- clinic-master
  {
    key: "allowMultipleTherapists",
    group: "clinicMaster",
    label: "שיוך מטופל לכמה מטפלים",
    description: "מאפשר לשייך מטופל ליותר ממטפל/ת אחד/ת",
    legacyDefault: false,
    enforcement: ["client", "rules", "functions"],
    sensitive: false,
    requires: null,
    clinicMaster: true,
    storage: "therapistPermissions",
    field: "allowMultipleTherapists",
    since: 1,
  },
  {
    key: "patientSharing.mode",
    group: "clinicMaster",
    label: "מצב שיתוף מטופלים",
    description: "assigned — מטופל משויך למטפל/ת; all — כל המטופלים משותפים לכל הצוות",
    type: "enum",
    values: ["assigned", "all"],
    default: "assigned",
    legacyDefault: "assigned",
    enforcement: ["client", "rules", "functions"],
    sensitive: "medical",
    requires: null,
    clinicMaster: true,
    storage: "multiClinicSettings",
    field: "patientSharingMode",
    since: 2,
  },
  {
    key: "patientSharing.allIncludesTreatments",
    group: "clinicMaster",
    label: "שיתוף כולל דוחות טיפול",
    description: "במצב 'כל המטופלים משותפים' — האם דוחות הטיפול משותפים גם הם",
    legacyDefault: true,
    enforcement: ["client", "rules", "functions"],
    sensitive: "medical",
    requires: null,
    clinicMaster: true,
    storage: "multiClinicSettings",
    field: "allSharedIncludesTreatments",
    since: 2,
  },
  {
    key: "scheduling.preventTherapistDoubleBooking",
    group: "clinicMaster",
    label: "חסימת כפל תורים למטפל",
    description: "כבוי — אזהרה בלבד; פעיל — חסימה (עקיפה למנהלים)",
    legacyDefault: false,
    enforcement: ["client"],
    sensitive: false,
    requires: null,
    clinicMaster: true,
    storage: "multiClinicSettings",
    field: "preventTherapistDoubleBooking",
    since: 2,
  },
  {
    key: "scheduling.blockBookingOnAbsence",
    group: "clinicMaster",
    label: "חסימת תורים בזמן היעדרות",
    description: "כבוי — אזהרה בלבד; פעיל — חסימה (עקיפה למנהלים)",
    legacyDefault: false,
    enforcement: ["client"],
    sensitive: false,
    requires: null,
    clinicMaster: true,
    storage: "multiClinicSettings",
    field: "blockBookingOnAbsence",
    since: 2,
  },
];

/** key → entry */
export const PERMISSIONS_BY_KEY = PERMISSIONS_REGISTRY.reduce((acc, entry) => {
  acc[entry.key] = entry;
  return acc;
}, {});

/** Every key (per-therapist + clinic-master). */
export const PERMISSION_KEYS = PERMISSIONS_REGISTRY.map((e) => e.key);

/** Keys that may be overridden per therapist (`therapist.permissions`). */
export const PER_THERAPIST_PERMISSION_KEYS = PERMISSIONS_REGISTRY.filter(
  (e) => !e.clinicMaster,
).map((e) => e.key);

/** Clinic-level-only keys. */
export const CLINIC_MASTER_PERMISSION_KEYS = PERMISSIONS_REGISTRY.filter(
  (e) => e.clinicMaster,
).map((e) => e.key);

/** Keys the Team UI renders disabled ("דורש תפקיד מנהל/ת קליניקה"). */
export const LOCKED_PERMISSION_KEYS = PERMISSIONS_REGISTRY.filter(
  (e) => e.locked === true,
).map((e) => e.key);

/**
 * Per-therapist keys that ALSO have a clinic-wide default in Clinic Settings
 * (`clinic.therapistPermissions.<key>`). When an admin has NOT explicitly
 * overridden one of these for a member, the key is OMITTED from the stored
 * `therapist.permissions` map so the clinic default keeps applying (resolver
 * step 4). Explicit per-member values are tracked in
 * `therapist.permissionsOverridden`.
 */
export const CLINIC_DEFAULT_PERMISSION_KEYS = [
  "allowFinanceAccess",
  "allowCreateInvoices",
  "allowExportData",
];

/** Legacy fallback for allowCreateInvoices (per-integration flags). */
export function legacyInvoiceFallback(clinic) {
  const morningAllow = clinic?.morningIntegration?.allowTherapistsToCreateInvoices;
  const diAllow = clinic?.digitalInvoiceIntegration?.allowTherapistsToCreateInvoices;
  if (morningAllow !== undefined || diAllow !== undefined) {
    return morningAllow === true || diAllow === true;
  }
  return undefined;
}

/**
 * The value a non-admin member gets for a per-therapist key when the therapist
 * doc has NO personal value (resolver steps 4–5): clinic.therapistPermissions
 * → legacy invoice fallback → registry legacyDefault. `requires` is NOT
 * applied here.
 */
export function getClinicDefaultValue(key, clinic) {
  const entry = PERMISSIONS_BY_KEY[key];
  if (!entry || entry.clinicMaster) return false;
  const cv = clinic?.therapistPermissions ? clinic.therapistPermissions[key] : undefined;
  if (cv !== undefined) return cv !== false && cv !== null;
  if (key === "allowCreateInvoices") {
    const legacy = legacyInvoiceFallback(clinic);
    if (legacy !== undefined) return legacy;
  }
  return entry.legacyDefault === true;
}

export function isPermissionKey(key) {
  return Object.prototype.hasOwnProperty.call(PERMISSIONS_BY_KEY, key);
}

export function isPerTherapistPermissionKey(key) {
  return isPermissionKey(key) && PERMISSIONS_BY_KEY[key].clinicMaster !== true;
}

/**
 * Derive the clinic role of a therapist doc. Legacy docs have no `clinicRole`;
 * `role` is never rewritten, so `role === "owner"` always wins.
 */
export function deriveClinicRole(therapist) {
  if (!therapist) return "therapist";
  if (therapist.role === "owner") return "owner";
  const stored = therapist.clinicRole;
  if (stored === "manager" || stored === "therapist" || stored === "secretary") {
    // A stored "manager" without isAdmin would be a corrupt doc; keep the
    // admin bypass as the single truth and trust isAdmin.
    if (stored === "manager" && therapist.isAdmin !== true) return "therapist";
    return stored;
  }
  return therapist.isAdmin === true ? "manager" : "therapist";
}

/** Admin bypass — identical to selectIsAdmin / isClinicAdmin / rules. */
export function isAdminTherapist(therapist) {
  return therapist?.role === "owner" || therapist?.isAdmin === true;
}

/** Patient sharing mode of a clinic ("assigned" unless explicitly "all"). */
export function getPatientSharingMode(clinic) {
  return clinic?.multiClinicSettings?.patientSharingMode === "all" ? "all" : "assigned";
}

/**
 * Role preset: explicit boolean for EVERY per-therapist key.
 * owner/manager ⇒ all true (informational; admin bypass applies anyway).
 * therapist/secretary ⇒ `roleDefaults[role]`, with the `all`-sharing override
 * applied when `clinic.multiClinicSettings.patientSharingMode === "all"`.
 * `requires` is enforced: a key whose prerequisite is false is forced false.
 */
export function getRolePreset(role, clinic) {
  const isAdminRole = role === "owner" || role === "manager";
  const sharesAll = getPatientSharingMode(clinic) === "all";
  const preset = {};
  PERMISSIONS_REGISTRY.forEach((entry) => {
    if (entry.clinicMaster) return;
    if (isAdminRole) {
      preset[entry.key] = true;
      return;
    }
    let v = entry.roleDefaults ? entry.roleDefaults[role] : undefined;
    if (
      sharesAll &&
      entry.roleDefaultsWhenAllShared &&
      entry.roleDefaultsWhenAllShared[role] !== undefined
    ) {
      v = entry.roleDefaultsWhenAllShared[role];
    }
    preset[entry.key] = v === true;
  });
  return applyRequires(preset);
}

/**
 * Force every key whose `requires` prerequisite is not true to false.
 * Iterates until stable so registry order never matters. Mutates + returns.
 */
export function applyRequires(perms) {
  for (let pass = 0; pass < PERMISSIONS_REGISTRY.length; pass += 1) {
    const changed = PERMISSIONS_REGISTRY.filter(
      (entry) =>
        !entry.clinicMaster &&
        entry.requires &&
        perms[entry.key] === true &&
        perms[entry.requires] !== true,
    );
    if (changed.length === 0) break;
    changed.forEach((entry) => {
      perms[entry.key] = false;
    });
  }
  return perms;
}

/** Presets for every assignable role against a legacy (assigned-mode) clinic. */
export const ROLE_PRESETS = {
  manager: getRolePreset("manager", null),
  therapist: getRolePreset("therapist", null),
  secretary: getRolePreset("secretary", null),
};

/**
 * Can this therapist own patients / appointments / be picked in an ownership
 * dropdown? Active and not a secretary.
 */
export function isAssignableTherapist(therapist) {
  if (!therapist) return false;
  if (therapist.isActive === false || therapist.isActive === null) return false;
  return deriveClinicRole(therapist) !== "secretary";
}

/**
 * Role BASELINE: what a member of `role` effectively gets with NO personal
 * overrides. Equals the role preset except for the CLINIC_DEFAULT keys of
 * non-admin roles, which take the clinic-wide default (Clinic Settings).
 * This is what the "אישי" chip and `permissionsOverridden` compare against.
 */
export function getRoleBaseline(role, clinic) {
  const preset = getRolePreset(role, clinic);
  if (role === "owner" || role === "manager") return preset;
  CLINIC_DEFAULT_PERMISSION_KEYS.forEach((key) => {
    preset[key] = getClinicDefaultValue(key, clinic);
  });
  return applyRequires(preset);
}

/**
 * Sanitize a client-supplied permissions map for a role: keeps only known
 * per-therapist boolean keys, fills gaps from the role preset, and enforces
 * `requires`. Shared by the Team UI and `updateTherapistRole`.
 *
 * Without `options` (legacy signature) returns the FULL map — an explicit
 * boolean for every per-therapist key.
 *
 * With `options.overriddenKeys` (override model) returns
 * `{ permissions, overridden }`:
 *   - start from the role preset;
 *   - keys listed in `overriddenKeys` take the client's boolean;
 *   - CLINIC_DEFAULT keys that are NOT overridden are OMITTED from
 *     `permissions`, so the clinic-wide default keeps applying;
 *   - `requires` is applied;
 *   - `overridden` = overriddenKeys ∩ per-therapist non-locked keys whose
 *     stored value differs from the role baseline (keys equal to the
 *     baseline are dropped — they are not personal any more).
 */
export function normalizePermissionsForRole(role, permissions, clinic, options) {
  const preset = getRolePreset(role, clinic);
  const legacyMode = !options || typeof options !== "object";
  const requested = new Set(
    !legacyMode && Array.isArray(options.overriddenKeys) ? options.overriddenKeys : [],
  );
  const out = {};
  PER_THERAPIST_PERMISSION_KEYS.forEach((key) => {
    const entry = PERMISSIONS_BY_KEY[key];
    const v = permissions ? permissions[key] : undefined;
    if (entry.locked === true) {
      // Locked keys follow the role, never the client.
      out[key] = preset[key];
    } else if (typeof v === "boolean" && (legacyMode || requested.has(key))) {
      out[key] = v;
    } else {
      out[key] = preset[key];
    }
  });
  applyRequires(out);
  if (legacyMode) return out;

  const baseline = getRoleBaseline(role, clinic);
  const overridden = PER_THERAPIST_PERMISSION_KEYS.filter(
    (key) =>
      requested.has(key) &&
      PERMISSIONS_BY_KEY[key].locked !== true &&
      out[key] !== baseline[key],
  );
  const stored = {};
  PER_THERAPIST_PERMISSION_KEYS.forEach((key) => {
    if (CLINIC_DEFAULT_PERMISSION_KEYS.includes(key) && !overridden.includes(key)) return;
    stored[key] = out[key];
  });
  return { permissions: stored, overridden };
}
